/* eslint-disable @next/next/no-img-element */
import Link from 'next/link';
import fromUnixTime from 'date-fns/fromUnixTime';
import { GetStaticPaths, GetStaticProps } from 'next';
import { NavigationItemFragment, NewsDetailItemFragment } from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import Content from '@amf/shared/components/content/Content';
import { useTranslate, TFnType } from '@tolgee/react';
import { ArrowLeftCircleIcon } from '@heroicons/react/24/solid';
import { formatDate } from '@amf/shared/utils/date';

import Layout from 'components/layout/Layout';
import { AppLinks } from 'utils/links';
import Meta from 'components/Meta';
import Container from 'components/layout/Container';
import Button from 'components/form/Button';

export function getNewsAuthor(news: NewsDetailItemFragment, t: TFnType): string {
  if (news.author.custom) {
    return news.author.custom;
  }

  if (news.author.internal) {
    return news.author.internal.givenName + ' ' + news.author.internal.familyName;
  }

  return t('news.detail.redaction');
}

interface NewsDetailPageProps extends NavigationProps {
  news: NewsDetailItemFragment;
}

export default function NewsDetailPage({ news, navigation, partners }: NewsDetailPageProps) {
  const { t } = useTranslate();
  const publishedAt = fromUnixTime(news.publishedAt);
  const author = getNewsAuthor(news, t);
  return (
    <div className='NewsDetailPage'>
      <Layout navigation={navigation} partners={partners}>
        <Meta title={news.title} description={news.perex} image={news.photoUrl} type={'article'} />
        <div className='min-h-[320px] sm:min-h-[400px] relative bg-neutral-700 flex items-center pt-12 sm:pt-40'>
          {news.photoUrl && (
            <>
              <img
                src={news.photoUrl}
                alt={news.title}
                className='absolute top-0 left-0 w-full h-full select-none pointer-events-none object-cover object-center'
              />
              <div className='absolute top-0 left-0 w-full h-full bg-gradient-to-b from-black/0 to-black/80' />
            </>
          )}
          <Container className='pt-24 pb-16 sm:py-24 relative z-10 flex flex-col sm:gap-3'>
            <Link href={AppLinks.newsList} passHref className='block w-full sm:w-auto'>
              <a className='mb-3 w-full sm:w-fit'>
                <Button variant='white' icon={ArrowLeftCircleIcon} className='w-full sm:w-auto'>
                  {t('news.detail.back')}
                </Button>
              </a>
            </Link>
            <h1 className='before:hidden m-0 font-black text-3xl sm:text-6xl font-primary text-white drop-shadow'>
              {news.title}
            </h1>
            <p className='text-xl sm:text-3xl text-white font-bold font-primary flex items-center gap-6 mt-3'>
              <span>{formatDate(publishedAt, 'dd/MM/yyyy, HH:mm')}</span>
              {news.categories.map(category => {
                if (!category) {
                  return null;
                }

                return <span key={category.id}>{category.name}</span>;
              })}
            </p>
          </Container>
        </div>
        <Container className='relative'>
          <div className='bg-white px-3 sm:px-10 py-3 sm:py-8 shadow-lg -translate-y-12 sm:-translate-y-1/2 -mb-12 sm:-mb-1/3'>
            <p className='text-lg sm:text-xl font-bold font-primary'>{news.perex}</p>
          </div>
          <Content content={news.content} />
        </Container>
      </Layout>
    </div>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const { newsList: news } = await sdk.NewsList({
    offset: 0,
    limit: 1000,
    union: process.env.NEXT_PUBLIC_UNION,
  });

  const paths: Array<string | { params: { path: string }; locale?: string }> = (news ?? [])
    .filter(n => !n.externalLink)
    .map(n => ({
      params: { path: n.paths.active },
    }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { path } = params as { path: string };

  let navigation: NavigationItemFragment[];
  let news: NewsDetailItemFragment | null | undefined;
  try {
    const data = await sdk.NewsDetailPage({
      path,
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale!),
    });
    news = data.news;
    navigation = data.navigation;
  } catch {
    return {
      notFound: true,
    };
  }

  if (!news) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      navigation,
      news,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
